// ./src/components/generic/ImageViewer/ImageViewer.tsx
import React, { useState, useCallback, useEffect, useRef } from "react";
import { Modal, ActionIcon, Group, Text, Box, Tooltip } from "@mantine/core";
import { useHotkeys } from "@mantine/hooks";
import { useTranslation } from "react-i18next";

import { downloadFile } from "@/utils/fileHelpers";

import { useAppEnvironment } from "@/contexts/AppEnvironmentContext";

import { AppIcon } from "../AppIcon";

// ============================================
// Types
// ============================================

export interface ImageViewerProps {
  /** Whether the viewer is open */
  opened: boolean;

  /** Callback to close the viewer */
  onClose: () => void;

  /** Image source URL */
  src: string;

  /** Alt text for the image */
  alt?: string;

  /** Filename for download (optional) */
  fileName?: string;

  /** Minimum zoom level (default: 1) */
  minZoom?: number;

  /** Maximum zoom level (default: 4) */
  maxZoom?: number;

  /** Zoom step for buttons (default: 0.25) */
  zoomStep?: number;
}

// ============================================
// Constants
// ============================================

const DEFAULT_MIN_ZOOM = 1;
const DEFAULT_MAX_ZOOM = 4;
const DEFAULT_ZOOM_STEP = 0.25;
const WHEEL_ZOOM_SENSITIVITY = 0.001;

// ============================================
// Shared styles
// ============================================

const actionIconStyles = {
  root: {
    color: "white",
    backgroundColor: "transparent",
    "--ai-hover": "rgba(255,255,255,0.1)",
    "--ai-disabled-color": "rgba(255,255,255,0.3)",
    "--ai-disabled-bg": "transparent",
  },
};

const gradientTop =
  "linear-gradient(to bottom, rgba(0,0,0,0.7) 0%, transparent 100%)";
const gradientBottom =
  "linear-gradient(to top, rgba(0,0,0,0.7) 0%, transparent 100%)";

// ============================================
// Helpers
// ============================================

/** Calculate the distance between two touch points */
const getTouchDistance = (touches: React.TouchList): number => {
  const dx = touches[0].clientX - touches[1].clientX;
  const dy = touches[0].clientY - touches[1].clientY;
  return Math.sqrt(dx * dx + dy * dy);
};

/** Calculate the midpoint between two touch points */
// const getTouchMidpoint = (touches: React.TouchList): { x: number; y: number } => ({
//   x: (touches[0].clientX + touches[1].clientX) / 2,
//   y: (touches[0].clientY + touches[1].clientY) / 2,
// });

// ============================================
// Component
// ============================================

export const ImageViewer: React.FC<ImageViewerProps> = ({
  opened,
  onClose,
  src,
  alt = "Image preview",
  fileName,
  minZoom = DEFAULT_MIN_ZOOM,
  maxZoom = DEFAULT_MAX_ZOOM,
  zoomStep = DEFAULT_ZOOM_STEP,
}) => {
  const { t } = useTranslation(["common"]);
  const { isMobile } = useAppEnvironment();

  // Zoom and pan state
  const [zoom, setZoom] = useState(1);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });

  // Pinch-to-zoom state
  const [isPinching, setIsPinching] = useState(false);
  const pinchStartDistanceRef = useRef(0);
  const pinchStartZoomRef = useRef(1);

  // Refs
  const containerRef = useRef<HTMLDivElement>(null);
  const imageRef = useRef<HTMLImageElement>(null);

  // We need a ref for zoom to use in touch handlers without stale closures
  const zoomRef = useRef(zoom);
  useEffect(() => {
    zoomRef.current = zoom;
  }, [zoom]);

  // Reset state when modal opens/closes
  useEffect(() => {
    if (opened) {
      setZoom(1);
      setPosition({ x: 0, y: 0 });
      setIsDragging(false);
      setIsPinching(false);
    }
  }, [opened]);

  // Reset position when zoom returns to 1
  useEffect(() => {
    if (zoom <= 1) {
      setPosition({ x: 0, y: 0 });
    }
  }, [zoom]);

  // Zoom handlers
  const handleZoomIn = useCallback(() => {
    setZoom((prev) => Math.min(prev + zoomStep, maxZoom));
  }, [zoomStep, maxZoom]);

  const handleZoomOut = useCallback(() => {
    setZoom((prev) => Math.max(prev - zoomStep, minZoom));
  }, [zoomStep, minZoom]);

  const handleResetZoom = useCallback(() => {
    setZoom(1);
    setPosition({ x: 0, y: 0 });
  }, []);

  // Mouse wheel zoom
  const handleWheel = useCallback(
    (e: React.WheelEvent) => {
      e.preventDefault();
      const delta = -e.deltaY * WHEEL_ZOOM_SENSITIVITY;
      setZoom((prev) => {
        const newZoom = prev + delta * prev; // Proportional zoom
        return Math.max(minZoom, Math.min(maxZoom, newZoom));
      });
    },
    [minZoom, maxZoom]
  );

  // Drag/pan handlers (mouse)
  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      if (zoom > 1) {
        e.preventDefault();
        setIsDragging(true);
        setDragStart({
          x: e.clientX - position.x,
          y: e.clientY - position.y,
        });
      }
    },
    [zoom, position]
  );

  const handleMouseMove = useCallback(
    (e: React.MouseEvent) => {
      if (isDragging && zoom > 1) {
        setPosition({
          x: e.clientX - dragStart.x,
          y: e.clientY - dragStart.y,
        });
      }
    },
    [isDragging, dragStart, zoom]
  );

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
  }, []);

  const handleMouseLeave = useCallback(() => {
    setIsDragging(false);
  }, []);

  // Touch handlers for mobile (single-finger drag + two-finger pinch-to-zoom)
  const handleTouchStart = useCallback(
    (e: React.TouchEvent) => {
      if (e.touches.length === 2) {
        // Start pinch-to-zoom
        e.preventDefault();
        setIsPinching(true);
        setIsDragging(false);
        pinchStartDistanceRef.current = getTouchDistance(e.touches);
        pinchStartZoomRef.current = zoomRef.current;
      } else if (e.touches.length === 1 && !isPinching) {
        // Start single-finger drag (only when zoomed in)
        if (zoomRef.current > 1) {
          const touch = e.touches[0];
          setIsDragging(true);
          setDragStart({
            x: touch.clientX - position.x,
            y: touch.clientY - position.y,
          });
        }
      }
    },
    [isPinching, position]
  );

  const handleTouchMove = useCallback(
    (e: React.TouchEvent) => {
      if (e.touches.length === 2 && isPinching) {
        // Handle pinch-to-zoom
        e.preventDefault();
        const currentDistance = getTouchDistance(e.touches);
        const scale = currentDistance / pinchStartDistanceRef.current;
        const newZoom = pinchStartZoomRef.current * scale;
        setZoom(Math.max(minZoom, Math.min(maxZoom, newZoom)));
      } else if (e.touches.length === 1 && isDragging && zoomRef.current > 1) {
        // Handle single-finger drag
        const touch = e.touches[0];
        setPosition({
          x: touch.clientX - dragStart.x,
          y: touch.clientY - dragStart.y,
        });
      }
    },
    [isPinching, isDragging, dragStart, minZoom, maxZoom]
  );

  const handleTouchEnd = useCallback(
    (e: React.TouchEvent) => {
      if (e.touches.length < 2) {
        setIsPinching(false);
      }
      if (e.touches.length === 0) {
        setIsDragging(false);
      }
    },
    []
  );

  // Download handler
  const handleDownload = useCallback(() => {
    downloadFile(src, fileName);
  }, [src, fileName]);

  // Keyboard shortcuts
  useHotkeys([
    ["Escape", onClose],
    ["Equal", handleZoomIn], // + key
    ["Minus", handleZoomOut], // - key
    ["0", handleResetZoom], // Reset zoom
  ]);

  // Double-click to toggle zoom
  const handleDoubleClick = useCallback(() => {
    if (zoom === 1) {
      setZoom(2);
    } else {
      handleResetZoom();
    }
  }, [zoom, handleResetZoom]);

  // Zoom percentage display
  const zoomPercentage = Math.round(zoom * 100);
  const isZoomReset = zoom === 1 && position.x === 0 && position.y === 0;

  // ---- Shared UI pieces ----

  const zoomControls = (
    <Group gap="xs">
      <Tooltip label={t("common:imageViewer.zoomOut")} position={isMobile ? "top" : "bottom"}>
        <ActionIcon
          variant="subtle"
          color="gray"
          size="lg"
          onClick={handleZoomOut}
          disabled={zoom <= minZoom}
          styles={actionIconStyles}
        >
          <AppIcon iconName="ZoomOut" size={20} />
        </ActionIcon>
      </Tooltip>

      <Text c="white" size="sm" w={50} ta="center">
        {zoomPercentage}%
      </Text>

      <Tooltip label={t("common:imageViewer.zoomIn")} position={isMobile ? "top" : "bottom"}>
        <ActionIcon
          variant="subtle"
          color="gray"
          size="lg"
          onClick={handleZoomIn}
          disabled={zoom >= maxZoom}
          styles={actionIconStyles}
        >
          <AppIcon iconName="ZoomIn" size={20} />
        </ActionIcon>
      </Tooltip>

      <Tooltip label={t("common:imageViewer.resetZoom")} position={isMobile ? "top" : "bottom"}>
        <ActionIcon
          variant="subtle"
          color="gray"
          size="lg"
          onClick={handleResetZoom}
          disabled={isZoomReset}
          styles={actionIconStyles}
        >
          <AppIcon iconName="Maximize" size={20} />
        </ActionIcon>
      </Tooltip>
    </Group>
  );

  const downloadButton = (
    <Tooltip label={t("common:imageViewer.download")} position={isMobile ? "top" : "bottom"}>
      <ActionIcon
        variant="subtle"
        color="gray"
        size="lg"
        onClick={handleDownload}
        styles={actionIconStyles}
      >
        <AppIcon iconName="Download" size={20} />
      </ActionIcon>
    </Tooltip>
  );

  const closeButton = (
    <Tooltip label={t("common:imageViewer.close")} position="bottom">
      <ActionIcon
        variant="subtle"
        color="gray"
        size="lg"
        onClick={onClose}
        styles={actionIconStyles}
      >
        <AppIcon iconName="X" size={20} />
      </ActionIcon>
    </Tooltip>
  );

  // ---- Layout renderers ----

  const renderDesktopHeader = () => (
    <Box
      pos="absolute"
      top={0}
      left={0}
      right={0}
      p="md"
      style={{ zIndex: 100, background: gradientTop }}
    >
      <Group justify="space-between" align="center">
        <Text c="white" size="sm" fw={500} lineClamp={1} maw="40%">
          {fileName || alt}
        </Text>

        <Group gap="xs">
          {zoomControls}

          <Box w={1} h={24} bg="rgba(255,255,255,0.3)" mx="xs" />

          {downloadButton}
          {closeButton}
        </Group>
      </Group>
    </Box>
  );

  const renderMobileHeader = () => (
    <Box
      pos="absolute"
      top={0}
      left={0}
      right={0}
      px="sm"
      py="md"
      style={{ zIndex: 100, background: gradientTop }}
    >
      <Group justify="space-between" align="center" wrap="nowrap">
        <Text c="white" size="sm" fw={500} lineClamp={1} style={{ flex: 1, minWidth: 0 }}>
          {fileName || alt}
        </Text>

        {closeButton}
      </Group>
    </Box>
  );

  const renderMobileFooter = () => (
    <Box
      pos="absolute"
      bottom={0}
      left={0}
      right={0}
      px="sm"
      pb="xl"
      pt="md"
      style={{ zIndex: 100, background: gradientBottom }}
    >
      <Group justify="center" align="center" gap="xs">
        {zoomControls}

        <Box w={1} h={24} bg="rgba(255,255,255,0.3)" mx={4} />

        {downloadButton}
      </Group>
    </Box>
  );

  // ---- Hint text ----

  const renderHint = () => {
    if (zoom !== 1) return null;

    // On mobile with bottom bar, show hint just above it
    // On desktop, show hint at the bottom
    const bottomOffset = isMobile ? 80 : 20;

    return (
      <Box
        pos="absolute"
        bottom={bottomOffset}
        left="50%"
        style={{ transform: "translateX(-50%)", zIndex: 100 }}
      >
        <Text c="rgba(255,255,255,0.5)" size="xs" ta="center">
          {isMobile
            ? t("common:imageViewer.hintMobile", {
              defaultValue:
                "Pinch to zoom • Double-tap to toggle zoom • Drag to pan when zoomed",
            })
            : t("common:imageViewer.hint", {
              defaultValue:
                "Scroll to zoom • Double-click to toggle zoom • Drag to pan when zoomed",
            })}
        </Text>
      </Box>
    );
  };

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      fullScreen
      withCloseButton={false}
      padding={0}
      styles={{
        content: {
          backgroundColor: "rgba(0, 0, 0, 0.95)",
        },
        body: {
          height: "100%",
          padding: 0,
        },
      }}
    >
      {/* Header */}
      {isMobile ? renderMobileHeader() : renderDesktopHeader()}

      {/* Footer (mobile only) */}
      {isMobile && renderMobileFooter()}

      {/* Image container */}
      <Box
        ref={containerRef}
        h="100%"
        w="100%"
        style={{
          overflow: "hidden",
          cursor: zoom > 1 ? (isDragging ? "grabbing" : "grab") : "default",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          touchAction: "none", // Prevent browser default touch gestures
        }}
        onWheel={handleWheel}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseLeave}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        onDoubleClick={handleDoubleClick}
      >
        <img
          ref={imageRef}
          src={src}
          alt={alt}
          draggable={false}
          style={{
            maxWidth: "100%",
            maxHeight: "100%",
            objectFit: "contain",
            transform: `scale(${zoom}) translate(${position.x / zoom}px, ${position.y / zoom}px)`,
            transition: isDragging || isPinching ? "none" : "transform 0.1s ease-out",
            userSelect: "none",
            WebkitUserSelect: "none",
          }}
        />
      </Box>

      {/* Hint */}
      {renderHint()}
    </Modal>
  );
};
